import { LogCreateOptions } from './logs.types';

export interface SubscriberResource {
    programSubscriberId: number;
    carrierName: string;
    capacity?: number;
    sharePercentage: number;
    programLimit?: number;
    programPercentage?: number;
    isParticipating: boolean;
}

export interface SubscriberParticipationResource {
    programSubscriberId: number;
    isParticipating: boolean;
}

export interface SubscriptionResource {
    id?: number;
    subscribers: SubscriberResource[];
}

export interface UwSubjectivity {
    subjectivityText: string;
}

export interface UnderwriterReview {
    id: string;
    policyFee: number;
    professionalFee: number;
    inspectionFee: number;
    slaFee: number;
    slaTax: number;
    homeState: string;
    lumenCommissionPercentage: number;
    brokerCommissionPercentage: number;
    premium: number;
    createdAt: string;
    narrative?: string;
    submissionId: number;
    subjectivities: UwSubjectivity[];
    hasQuote: boolean;
    isBrokerCommissionRated: boolean;
    inForce: boolean;
    primaryLimit?: string;
    productLineTypeCode: string;
    triaPercentage?: number;
    HNOPremium?: number;
    grossPremium?: number;
    isLeader?: boolean;
    policyLimitOfLiability?: number;
    lumenLimitOfLiability?: number;
    followedPolicyNumber?: string;
    followedPolicyCoverageForm?: string;
    followedPolicyInsurer?: string;
    maximumBrokerCommissionPercentage?: number;
    // Add program premium to store non-participation premium (no TRIA)
    programPremium?: number;
    subscription?: SubscriptionResource;
    locationReviews?: UwLocationReview[] & UwGLLocationReview[] & UwBRLocationReview[];
}

export interface UwGridRow {
    locationName?: string;
    finalRate?: number;
    totalPremium?: number;
    locationId?: string;
    zone?: string;
    tiv?: number;
    dedBBPremium?: number;
    baseRate?: number;
    CR_DB?: number;
    lumenRating?: number;
}

export interface UwLocationReview {
    rowId?: string; // TODO: fix name to be locationId
    locationReviewId?: string;
    AOP?: string;
    AOP_DED?: string;
    WHH?: string;
    WHH_DED?: string;
    NS?: string;
    NS_DED?: string;
    FL?: string;
    FL_LIMIT?: string;
    FL_DED?: string;
    EQ?: string;
    EQ_LIMIT?: string;
    EQ_DED?: string;
    baseRate?: number;
    CR_DB?: number;
    finalRate?: number;
    triaPercentage?: number;
    totalInsurableValue?: number;
    totalPremium?: number;
    endorsementPremium?: number; // this doesn't come from the server, but is used temporarily to house the prorata premium for endorsements
    insp?: string;
    inspectionsRequiredCount?: number;
    lumenRating?: number;
    locationName?: string;
    locationZone?: string;
    expiringBaseRate?: number;
    expiringCrdb?: number;
    expiringFinalRate?: number;
}

export interface UwGLLocationReview {
    rowId?: string; // TODO: fix name to be locationId
    glLocationReviewId?: string;
    genAggLimit?: string;
    premOpsOccurrenceLimit?: string;
    HNOLimit?: string;
    premOpsDeductible?: string;
    baseRatedPremium?: number;
    experienceCRDB?: number;
    irpm?: number;
    scoreIrpm?: number;
    ratingScore?: string;
    triaPercentage?: number;
    totalPremium?: number;
    insp?: string;
    endorsementPremium?: number; // this doesn't come from the server, but is used temporarily to house the prorata premium for endorsements
    blendedDoorRate?: number; // this doesn't come from the server, but is used temporarily to house the blended door rate calculation
    inspectionsRequiredCount?: number;
    numberUnits?: number;
    numberPools?: number;
    locationName?: string;
    locationAddress?: string;
    expiringBaseRatedPremium?: number;
    expiringExperienceCRDB?: number;
    expiringIrpm?: number;
    ratedExposures?: GLRatingExposure[];
    annualizedPremium?: number;
}

export interface GLRatingExposure {
    exposureBasis: string;
    exposureClassCode: string;
    exposureValue: number;
    exposureFinalPremium: number;
    exposureFinalRate: number;
    exposureBaseRate: number;
    exposureBasePremium: number;
    exposureLossCosts: number;
    exposureLCM: number;
    exposureILF: number;
}

export interface UwReview {
    underwriterReview: UnderwriterReview;
    locationReviews: UwLocationReview[] & UwGLLocationReview[] & UwBRLocationReview[];
}

export interface Taxes {
    slaFee: any;
    slaTax: any;
}

export interface AdjustmentReviewLocationData {
    diffPrem: number;
    grossPrem: number;
    proRataPrem: number;

    locationName: string;
    locationId: string;
    finalRate: number;
    origRate: number;
    triaPercentage: number;
    origTiv: number;
    newTiv: number;
    effectiveTiv: number;
    origPremium: number;
    propertyReview?: UwLocationReview;
}

export interface AdjustmentReviewGLLocationData {
    locationName: string;
    locationId: string;
    triaPercentage: number;
    origExposures: GLRatingExposure[];
    origPremium: number;
    origBaseRatedPremPaid: number;
    baseRatedPremium: number;
    propertyGLReview?: UwGLLocationReview;
    origUnits: number;
    origPools: number;
    units: number;
    pools: number;
    origIrpm: number;
    irpm: number;
    HNOPremium: number;
    grossPrem: number;
    proRataPrem: number;
    annualizedGrossPrem: number;
}

export interface AdjustmentReviewBRLocationData {
    locationName: string;
    locationId: string;
    locationStates: Map<AdjustmentReviewBRLocationStateTypes, AdjustmentReviewBRLocationState>;
    propertyReview: UwBRLocationReview;
}

export enum AdjustmentReviewBRLocationStateTypes {
    ORIGINAL = 'ORIGINAL',
    CURRENT = 'CURRENT',
    UPDATED = 'UPDATED',
}

export interface AdjustmentReviewBRLocationState {
    effectiveDate: string;
    hardCosts: number;
    softCosts: number;
    lossOfGrossEarnings: number;
    lossOfRentalIncome: number;
    tcv: number;
    finalRate: number;
    chargedPremium: number;
    programPremium: number;
}

export interface DedBuyBackData {
    totalPrem: string;
    locationId: string;
    buyBackDed: string;
}

export interface AdjustmentReviewData {
    totalPrem: number;
    homeState: string;
    locationData: AdjustmentReviewLocationData[];
    origEffectiveDate: string;
    origExpirationDate: string;
    newEffectiveDate: string;
    newExpirationDate: string;
    isRateRetroactive: boolean;
    isBrokerCommissionRated: boolean;
    brokerCommissionPercentage: number;
}

export interface AdjustmentGLReviewData {
    totalPrem: number;
    homeState: string;
    locationData: AdjustmentReviewGLLocationData[];
    origEffectiveDate: string;
    origExpirationDate: string;
    newEffectiveDate: string;
    newExpirationDate: string;
    isRateRetroactive: boolean;
    isBrokerCommissionRated: boolean;
    brokerCommissionPercentage: number;
}

export interface AdjustmentBRReviewData {
    totalPrem: number;
    locationData: AdjustmentReviewBRLocationData[];
    origEffectiveDate: string;
    origExpirationDate: string;
    currentEffectiveDate: string;
    updatedEffectiveDate: string;
    updatedExpirationDate?: string;
    isRateRetroactive: boolean;
    isBrokerCommissionRated: boolean;
    brokerCommissionPercentage: number;
    narrative: string;
    underwriterReview?: UnderwriterReview;
    inForceUnderwriterReview: UnderwriterReview;
}

export interface RestoreLocationsRequest {
    locationIds: number[];
    productLineTypeCode: string;
    restoreRequestMessage?: LogCreateOptions;
}

export interface UnderwritingReviewRequest {
    locationReviews?: any[];

    policyFee?: number | string;

    professionalFee?: number | string;

    agentCommissionOverride?: number | string;

    HNOPremium?: number;

    triaPercentage?: number;

    subjectivities?: any[];

    subscriberParticipations?: SubscriberParticipationResource[];

    isDraft?: boolean;

    referenceUWId?: string;
    primaryLimit?: string;

    productLineTypeCode?: string;
    maximumBrokerCommissionPercentage?: number;

    submissionId: string;

    totalPremium?: number | null;
}

export const BrCatastropheLimitType = {
    SEPARATE: 'SEPARATE',
    COMBINED: 'COMBINED',
};

export interface UwBRLocationReview {
    locationId?: number;
    locationName?: string;
    locationZone?: string;
    totalContractValue?: number;
    brLocationReviewId?: number;
    projectTypeCode?: string;
    catastropheLimitType?: string;
    brCatastropheLimit?: number;
    delayInCompletionCatastropheLimit?: number;
    combinedCatastropheLimit?: number;
    additionalDebrisRemovalExpenses?: number;
    emergencyRemoval?: number;
    fraudAndDeceit?: number;
    limitedFungusCoverage?: number;
    waterborneProperty?: number;
    blueprintsAndConstructionDocuments?: number;
    claimPreparationExpense?: number;
    constructionTrailers?: number;
    expeditingExpenses?: number;
    expenseToReErectScaffolding?: number;
    fireDepartmentServiceCharges?: number;
    ordinanceOrLawUndamagedParts?: number;
    ordinanceOrLawIncreasedCost?: number;
    personalProperty?: number;
    pollutantCleanupAndRemoval?: number;
    rechargeOfFireExtinguishers?: number;
    rewards?: number;
    sewerBackup?: number;
    temporaryStorageLocations?: number;
    transit?: number;
    treesShrubsAndPlants?: number;
    deductible?: number;
    permissionToOccupyDays?: number;
    periodOfIndemnity?: number;
    waitingPeriod?: number;
    softCost30DayLimitAmount?: number;
    hasSoftCost30DayLimit?: boolean;
    rentalIncome30DayLimitAmount?: number;
    hasRentalIncome30DayLimit?: boolean;
    income30DayLimitAmount?: number;
    hasIncome30DayLimit?: boolean;
    zone?: string;
    baseRate?: number;
    modeled?: number;
    additionalCatExposure?: number;
    renovationModifier?: number;
    midTermPlacement?: number;
    totalRateAdditions?: number;
    deductibleStructure?: number;
    contractorQuality?: number;
    siteSecurity?: number;
    leadCarrier?: number;
    constructionModifier?: number;
    totalRateAdjustment?: number;
    softCostMultiplier?: number;
    lossOfRentsMultiplier?: number;
    lossOfGrossEarningsMultiplier?: number;
    finalRate?: number;
    hardCostPremium?: number;
    softCostFinalRate?: number;
    softCostPremium?: number;
    lossOfRentalIncomeFinalRate?: number;
    lossOfRentalIncomePremium?: number;
    lossOfGrossEarningsFinalRate?: number;
    lossOfGrossEarningsPremium?: number;
    hasTria?: boolean;
    triaPercentage?: number;
    // program premium excludes TRIA and participation percentage
    programPremium?: number;
    // totalPremium may be null when participation limits are not set
    totalPremium?: number;
    hasEnr400Contractor?: boolean;
    namedWindstormDeductible?: string;
    earthMovementDeductible?: string;
    floodDeductible?: string;
    isWaterDamageCovered?: boolean;
    waterDamageSublimit?: number;
    isEarthMovementCovered?: boolean;
    earthMovementSublimit?: number;
    isFloodCovered?: boolean;
    floodSublimit?: number;
    isWindstormCovered?: boolean;
    isNamedWindstormCovered?: boolean;
    windstormSublimit?: number;
    namedWindstormSublimit?: number;
    waterDamageDeductible?: string;
    hasWaterDamageSublimit?: boolean;
    hasEarthMovementSublimit?: boolean;
    hasFloodSublimit?: boolean;
    hasWindstormSublimit?: boolean;
    hasNamedWindstormSublimit?: boolean;
    underwriterAdjustedPremium?: number;
    underwriterAdjustmentFactor?: number;
    technicalFinalRate?: number;
    automaticExtensionPeriod?: string;
    automaticExtensionRate?: number;
    endorsementPremium?: number; // this doesn't come from the server, but is used temporarily to house the prorata premium for endorsements
    inspectionsRequiredCount?: number;
}
